import { useEffect, useRef } from "react";

// Ambient studio soundscape: diffused hum + film flutter near the cinema room.
export function useStudioAudio(enabled) {
  const ctxRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);

    // brown noise hum (ventilation / stage power)
    const len = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    noise.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 240;
    const humGain = ctx.createGain();
    humGain.gain.value = 0.045;
    noise.connect(lp).connect(humGain).connect(master);
    noise.start();

    // 50 Hz mains hum
    const osc = ctx.createOscillator();
    osc.frequency.value = 50;
    osc.type = "sine";
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.008;
    osc.connect(oscGain).connect(master);
    osc.start();

    // projector: real film flutter — band-passed noise modulated at 24 fps
    const projGain = ctx.createGain();
    projGain.gain.value = 0;
    projGain.connect(master);

    const flutter = ctx.createGain();
    flutter.gain.value = 0.55;
    flutter.connect(projGain);

    const filmNoise = ctx.createBufferSource();
    filmNoise.buffer = buf;
    filmNoise.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1100;
    bp.Q.value = 0.8;
    filmNoise.connect(bp).connect(flutter);
    filmNoise.start();

    const lfo = ctx.createOscillator();
    lfo.type = "triangle";
    lfo.frequency.value = 24;
    const lfoAmp = ctx.createGain();
    lfoAmp.gain.value = 0.42;
    lfo.connect(lfoAmp).connect(flutter.gain);
    lfo.start();

    const reel = ctx.createOscillator();
    reel.type = "sine";
    reel.frequency.value = 96;
    const reelGain = ctx.createGain();
    reelGain.gain.value = 0.05;
    reel.connect(reelGain).connect(projGain);
    reel.start();

    const projTimer = setInterval(() => {
      const p = window.__camPos;
      // the projector is only audible inside the auditorium (x 25 → 45)
      const inside = p && p.x > 25 && p.z > -13 && p.z < 5;
      const door = inside ? Math.min(1, (p.x - 25) / 2.5) : 0;
      const d = inside ? Math.hypot(p.x - 38, p.z + 4) : 99;
      const g = door * Math.max(0, 1 - d / 16) * 0.22;
      projGain.gain.setTargetAtTime(g, ctx.currentTime, 0.5);
    }, 320);

    return () => {
      clearInterval(projTimer);
      ctx.close();
      ctxRef.current = null;
    };
  }, [enabled]);
}
